import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Container, Button, Row, Col, Table } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import getValidationErrors from '../../../utils/getValidationErrors';

import { IParcelas } from '../../../pages/CartaoSaudeDetalhe';

import { optionsVencimentoCartao, IOptions } from '../../../utils/options';

import SelectAsync from '../../SelectAsync';
import SelectSync, { IOption } from '../../SelectSync';
import Loading from '../../Loading';
import getResponseErrors from '../../../utils/getResponseErrors';
import { useAuth } from '../../../hooks/auth';
import InputMoney from '../../InputMoney';
import Toggle from '../../Exame/Toggle';

type ComponentProps = {
  handleFecharComponent(PlaCodigo: number): void;
};

interface ISubmitForm {
  PacCodigo: string;
  PlaTipo: string;
  PlaDiaVencimento: string;
  PlaNegociacao?: boolean;
  PlaValorPorPessoa?: number;
  PlaValorInclusao?: number;
}

const CartaoSaudeCadastro: React.FC<ComponentProps> = ({
  handleFecharComponent,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [simulouParcelas, setSimulouParcelas] = useState(false);
  const [parcelas, setParcelas] = useState<IParcelas[]>();
  const [optionsTipo, setOptionsTipo] = useState<IOptions[]>();
  const [plaTipo, SetPlaTipo] = useState<string>();
  const [ehEmpresarial, setEhEmpresarial] = useState(false);
  const [urlBuscaPaciente, SetUrlBuscaPaciente] = useState(
    'pacientes/pacientesemcartao?termoBusca=',
  );
  const [negociacao, setNegociacao] = useState(false);

  const { user, clinica } = useAuth();

  const optionVencimento = useMemo(() => {
    if (clinica.CliDiasVencimento && clinica.CliDiasVencimento !== '') {
      // dr saude
      const dias = clinica.CliDiasVencimento.split(',').map(dia => {
        return {
          value: dia.toString(),
          label: dia.toString(),
        };
      });
      return dias;
    }

    return optionsVencimentoCartao;
  }, [clinica.CliDiasVencimento]);

  const handleVerificaTipocartao = useCallback(
    (Platipo: string, options: IOptions[]) => {
      const tipoCartaotexto =
        options.find(opt => opt.value === Platipo)?.label || 'NORMAL';

      const pessoaJuridica =
        tipoCartaotexto.includes('EMPRESA') ||
        tipoCartaotexto.includes('EMPRESARIAL');

      const urlBusca = pessoaJuridica
        ? `pacientes/pacientesemcartao?PacPessoaFisica=${!pessoaJuridica}&PlaTipo=${Platipo}&termoBusca=`
        : 'pacientes/pacientesemcartao?termoBusca=';

      SetUrlBuscaPaciente(urlBusca);

      setEhEmpresarial(pessoaJuridica);
    },
    [],
  );

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IOption[]>('planos/tiposcartao');

        const optionsAtivos = response.data.filter(
          opt => opt.isDisabled === false,
        );

        setOptionsTipo(optionsAtivos);

        const primeiroTipo = optionsAtivos[0];

        handleVerificaTipocartao(primeiroTipo.value, optionsAtivos);

        SetPlaTipo(primeiroTipo.value);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar planos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    loadDados();
  }, [addToast, handleVerificaTipocartao]);

  const initialValue = {
    PlaDiaVencimento: optionVencimento ? optionVencimento[0].value : '5',
    PlaTipo: plaTipo,
  };

  const formRefBusca = useRef<FormHandles>(null);

  const handleSubmitForm = useCallback(
    async (dados: ISubmitForm) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          PacCodigo: Yup.string().required('Titular é obrigatório'),
          PlaTipo: Yup.string().required('Obrigatório'),
          PlaDiaVencimento: Yup.string().required('Obrigatório'),
          PlaNegociacao: Yup.boolean().nullable(),
          PlaValorPorPessoa: Yup.number().nullable(),
          PlaValorInclusao: Yup.number().nullable(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicio = new Date();

        const { PlaNegociacao, PlaValorPorPessoa, PlaValorInclusao } = dados;

        if (PlaNegociacao && PlaValorPorPessoa && PlaValorPorPessoa < 5)
          throw new Error('Valor da negociação não pode ser menor que R$5,00');

        const criaCartao = {
          PlaDataInicio: dataInicio,
          PlaTipo: dados.PlaTipo,
          PlaDiaVencimento: dados.PlaDiaVencimento,
          PlaNegociacao,
          PlaValorPorPessoa,
          PlaValorInclusao,
          dependentes: [
            {
              DepPacCodigo: dados.PacCodigo,
              DepParentesco: 'Titular',
              DepTitular: true,
            },
          ],
        };

        const response = await api.post('planos', criaCartao);

        handleFecharComponent(response.data.PlaCodigo);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
          addToast({
            title: 'Não foi possível salvar o cartão',
            type: 'error',
          });
          return;
        }

        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível salvar os dependentes',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, handleFecharComponent],
  );

  const handleSimulaParcelas = useCallback(async () => {
    setLoading(true);
    try {
      const dadosForm = formRefBusca.current?.getData();

      if (!dadosForm) throw new Error('Erro ao recuperar dados do formulário');

      const {
        PacCodigo,
        PlaNegociacao,
        PlaValorPorPessoa,
        PlaValorInclusao,
        PlaDiaVencimento,
        PlaTipo,
      } = dadosForm;

      if (!PlaDiaVencimento || !PlaTipo || !PacCodigo)
        throw new Error(
          'Titular, Tipo, Data início e Dia de Vencimento devem ser preenchidos',
        );

      const dataInicio = new Date();

      if (PlaNegociacao && PlaValorPorPessoa && PlaValorPorPessoa < 5)
        throw new Error('Valor da negociação não pode ser menor que R$5,00');

      const criaCartao = {
        PlaDataInicio: dataInicio,
        PlaDiaVencimento,
        PlaTipo,
        PlaNegociacao,
        PlaValorPorPessoa,
        PlaValorInclusao,
        CliCodigo: clinica.CliCodigo,
      };

      const response = await api.post<IParcelas[]>(
        'planos/simulaparcelas',
        criaCartao,
      );

      const parcelasFormatadas = response.data.map(par => {
        return {
          ...par,
          CprDataVencFormatado: format(
            parseISO(par.CprDataVenc.toString()),
            'dd/MM/yyyy',
          ),
        };
      });

      setParcelas(parcelasFormatadas);
      setSimulouParcelas(true);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
        addToast({
          title: 'Não foi possível calcular parcelas',
          type: 'error',
        });
        return;
      }

      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível calcular parcelas',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, clinica.CliCodigo]);

  const handleApagaSimulacao = useCallback(() => {
    setParcelas(undefined);
    setSimulouParcelas(false);
  }, []);

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}
      {optionsTipo && (
        <Form
          ref={formRefBusca}
          initialData={initialValue}
          onSubmit={handleSubmitForm}
        >
          <Row noGutters>
            <Col sm={12} className="pr-1">
              <SelectSync
                name="PlaTipo"
                label="Tipo"
                options={optionsTipo}
                handleSelectChange={
                  dados => handleVerificaTipocartao(dados.value, optionsTipo)
                  // eslint-disable-next-line react/jsx-curly-newline
                }
                required
              />
            </Col>
          </Row>
          <Row noGutters className="mt-2">
            <Col sm={12} className="pr-1">
              <SelectAsync
                name="PacCodigo"
                label={
                  ehEmpresarial ? 'Empresa Responsável' : 'Nome do Titular'
                }
                isClearable
                urlApi={urlBuscaPaciente}
                placeholder="Digite para buscar"
                handleSelectChange={() => console.log('')}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Row>
                <Col sm={5} className="pr-1">
                  <SelectSync
                    name="PlaDiaVencimento"
                    label="Dia Vencimento"
                    options={optionVencimento}
                    handleSelectChange={handleApagaSimulacao}
                    required
                  />
                </Col>
                {user.UsuTipo <= clinica.CliPerfilValorCartao && (
                  <>
                    <Col sm={12} className="pr-1 pt-2">
                      <Toggle
                        nome="PlaNegociacao"
                        label="Negociação de valores?"
                        classSpam={{ 'font-weight': 'bold' }}
                        handleOnChange={() => {
                          handleApagaSimulacao;
                          setNegociacao(!negociacao);
                        }}
                        isDisabled={false}
                      />
                      {/* <SelectSync
                    name="PlaNegociacao"
                    label=""
                    options={optionsSimNao}
                    handleSelectChange={handleApagaSimulacao}
                  /> */}
                    </Col>
                    {negociacao && (
                      <>
                        <Col sm={6} className="pr-1">
                          <InputMoney
                            name="PlaValorPorPessoa"
                            label="Valor Por Pessoa"
                          />
                        </Col>
                        <Col sm={6} className="pr-1">
                          <InputMoney
                            name="PlaValorInclusao"
                            label="Valor Inclusão Dependente"
                          />
                        </Col>
                      </>
                    )}
                  </>
                )}

                <Col sm={12} className="mb-2">
                  <h6 className="my-4">
                    <b>Caso tenha dependentes adicione após o cadastro</b>
                  </h6>
                  <Button
                    type="button"
                    size="sm"
                    className="mr-2"
                    variant={simulouParcelas ? 'outline-secondary' : 'success'}
                    onClick={handleSimulaParcelas}
                  >
                    Simular parcelas
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    variant={simulouParcelas ? 'success' : 'outline-secondary'}
                    disabled={!simulouParcelas}
                  >
                    CADASTRAR
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              {simulouParcelas && parcelas && (
                <>
                  <h6>Simulação Parcelas</h6>
                  <Table className="text-secondary" hover size="sm">
                    <thead>
                      <tr className="bg-light text-dark">
                        <th>Parcela</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parcelas?.map(par => (
                        <tr key={`parc-${par.CprHistorico}`}>
                          <td>{par.CprHistorico}</td>
                          <td>{par.CprDataVencFormatado}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </Col>
          </Row>
        </Form>
      )}
    </Container>
  );
};

export default CartaoSaudeCadastro;
